import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="stopwatch"
export default class extends Controller {
  static targets = ["display", "startStopButton", "resetButton"];

  connect() {
    console.log("Stopwatch controller connected");
    this.loadState();
    this.applyMedievalText(); // Apply medieval text on connect
    document.addEventListener(
      "theme:change",
      this.applyMedievalText.bind(this)
    ); // Listen for theme changes

    // Resume timer if it was running before
    if (this.running) {
      // Don't call start() as it would reset startTime
      this.interval = setInterval(() => this.updateDisplay(), 10);
    }
    this.updateDisplay();
    this.updateButtonState();
  }

  disconnect() {
    // Just clear the interval, but don't reset the state
    if (this.running) {
      clearInterval(this.interval);
    }
    document.removeEventListener(
      "theme:change",
      this.applyMedievalText.bind(this)
    );
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.startTime = Date.now() - this.elapsedTime;
    this.interval = setInterval(() => this.updateDisplay(), 10);
    this.updateButtonState();
    this.saveState();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    clearInterval(this.interval);
    this.elapsedTime = Date.now() - this.startTime;
    this.updateButtonState();
    this.saveState();
  }

  reset() {
    this.stop();
    this.elapsedTime = 0;
    this.startTime = 0;
    this.updateDisplay();
    this.updateButtonState();
    this.saveState();
  }

  toggle() {
    if (this.running) {
      this.stop();
    } else {
      this.start();
    }
  }

  updateDisplay() {
    const time = this.running ? Date.now() - this.startTime : this.elapsedTime;
    const milliseconds = String(Math.floor(time % 1000)).padStart(3, "0");
    const totalSeconds = Math.floor(time / 1000);
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    this.displayTarget.textContent = `${hours}:${minutes}:${seconds}.${milliseconds}`;
  }

  updateButtonState() {
    const theme = document.documentElement.getAttribute("data-bs-theme");
    if (this.running) {
      this.startStopButtonTarget.textContent =
        theme === "medieval" ? "Halt" : "Stop";
      this.startStopButtonTarget.classList.remove("btn-primary");
      this.startStopButtonTarget.classList.add("btn-danger");
      this.resetButtonTarget.disabled = true;
    } else {
      this.startStopButtonTarget.textContent =
        theme === "medieval" ? "Commence" : "Start";
      this.startStopButtonTarget.classList.remove("btn-danger");
      this.startStopButtonTarget.classList.add("btn-primary");
      this.resetButtonTarget.disabled = false;
    }
    this.applyMedievalTextForElement(this.startStopButtonTarget); // Re-apply medieval text for the button
  }

  // Save stopwatch state to localStorage
  saveState() {
    const state = {
      running: this.running,
      elapsedTime: this.elapsedTime,
      startTime: this.startTime,
      timestamp: Date.now(),
    };
    localStorage.setItem("stopwatchState", JSON.stringify(state));
  }

  // Load stopwatch state from localStorage
  loadState() {
    const savedState = localStorage.getItem("stopwatchState");
    if (savedState) {
      const state = JSON.parse(savedState);
      this.running = state.running;

      // If it was running, calculate how much time has passed since the page was closed
      if (this.running) {
        const timePassed = Date.now() - state.timestamp;
        this.elapsedTime = state.elapsedTime + timePassed;
      } else {
        this.elapsedTime = state.elapsedTime;
      }

      this.startTime = state.startTime;
    } else {
      // Default state if no saved state exists
      this.reset();
    }
  }

  applyMedievalText() {
    const theme = document.documentElement.getAttribute("data-bs-theme");
    if (theme === "medieval") {
      this.element
        .querySelectorAll("[data-medieval-text]")
        .forEach((element) => {
          if (!element.hasAttribute("data-original-text")) {
            element.setAttribute(
              "data-original-text",
              element.textContent.trim()
            );
          }
          // Update button text based on running state if it's the start/stop button
          if (element === this.startStopButtonTarget) {
            element.textContent = this.running ? "Halt" : "Commence";
          } else if (element.hasAttribute("data-medieval-text")) {
            element.textContent = element.getAttribute("data-medieval-text");
          }
        });
    } else {
      this.element
        .querySelectorAll("[data-original-text]")
        .forEach((element) => {
          element.textContent = element.getAttribute("data-original-text");
          // Reset medieval attribute tracking for buttons
          // element.removeAttribute('data-original-text'); // Keep original text for switching back
        });
      // Ensure button text is correct in non-medieval themes
      this.updateButtonState();
    }
  }

  // Helper to apply medieval text to a single element respecting its state
  applyMedievalTextForElement(element) {
    const theme = document.documentElement.getAttribute("data-bs-theme");
    if (theme === "medieval" && element.hasAttribute("data-medieval-text")) {
      if (!element.hasAttribute("data-original-text")) {
        element.setAttribute("data-original-text", element.textContent.trim()); // Store the *current* text as original if not set
      }
      if (element === this.startStopButtonTarget) {
        element.textContent = this.running ? "Halt" : "Commence";
      } else if (element.hasAttribute("data-medieval-text")) {
        element.textContent = element.getAttribute("data-medieval-text");
      }
    } else if (element.hasAttribute("data-original-text")) {
      // If not medieval, restore original text (which might be Start/Stop)
      // The updateButtonState method will handle setting the correct non-medieval text
      element.textContent = element.getAttribute("data-original-text");
      // Ensure correct Start/Stop text is set based on state
      if (element === this.startStopButtonTarget) {
        element.textContent = this.running ? "Stop" : "Start";
      }
    }
  }
}
